import { PROMPT_BODY_MAX, roleAtLeast, type AgentView } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { createAgent, fetchAgents, fetchPrompts } from '../api/agents.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field, SelectField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { cn } from '../lib/cn.js'
import { formatRelative } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

/**
 * Что отдаёт форма создания. Размеченное объединение, а не объект с двумя
 * необязательными полями: «текст и версия промпта одновременно» — состояние,
 * которого не должно существовать, и лучше сделать его невыразимым здесь,
 * чем ловить отказом сервера.
 */
type CreateFormValue =
  | { mode: 'new'; name: string; description: string; body: string }
  | { mode: 'existing'; name: string; description: string; promptVersionId: string }

export function AgentsPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const canEdit = roleAtLeast(workspace.role, 'admin')
  const [showForm, setShowForm] = useState(false)

  const agents = useQuery({
    queryKey: ['agents', workspace.id],
    queryFn: () => fetchAgents(workspace.id),
  })

  const prompts = useQuery({
    queryKey: ['prompts', workspace.id],
    queryFn: () => fetchPrompts(workspace.id),
    enabled: showForm,
  })

  const create = useMutation({
    mutationFn: (input: CreateFormValue) =>
      createAgent(workspace.id, {
        name: input.name,
        description: input.description,
        config: {},
        /**
         * Одно из двух: либо текст новой инструкции, либо выбранная
         * версия существующего промпта. Сервер в первом случае заводит
         * промпт сам, одной транзакцией с агентом.
         */
        ...(input.mode === 'new'
          ? { systemPrompt: { body: input.body, name: '' }, prompts: [] }
          : { prompts: [{ role: 'system' as const, promptVersionId: input.promptVersionId }] }),
        note: 'первая сборка',
        publish: true,
      }),
    onSuccess: (agent) => {
      void queryClient.invalidateQueries({ queryKey: ['agents', workspace.id] })
      /**
       * Список промптов тоже сбрасывается: создание агента могло завести
       * промпт. Без этого раздел «Промпты» показывает список без свежей
       * записи — она появляется только после перезагрузки страницы.
       * Поймано живым прогоном, а не рассуждением.
       */
      void queryClient.invalidateQueries({ queryKey: ['prompts', workspace.id] })
      setShowForm(false)
      void navigate(`/w/${workspace.id}/agents/${agent.id}`)
    },
  })

  const columns: Column<AgentView>[] = [
    {
      key: 'name',
      header: 'Агент',
      render: (row) => (
        <div>
          <Link
            to={`/w/${workspace.id}/agents/${row.id}`}
            className="font-medium text-accent hover:underline"
          >
            {row.name}
          </Link>
          {row.description && <p className="mt-0.5 text-xs text-muted">{row.description}</p>}
        </div>
      ),
    },
    {
      key: 'published',
      header: 'Опубликована',
      render: (row) =>
        row.publishedVersion ? (
          <Badge tone="ok">версия {row.publishedVersion.version}</Badge>
        ) : (
          <Badge tone="warn">ни одна не опубликована</Badge>
        ),
    },
    {
      key: 'prompt',
      header: 'Системная инструкция',
      render: (row) => {
        const system = row.publishedVersion?.prompts.find((item) => item.role === 'system')

        return system ? (
          <span className="text-ink-soft">
            {system.promptName}{' '}
            <span className="text-muted">· версия {system.promptVersion}</span>
          </span>
        ) : (
          <span className="text-muted">не задана</span>
        )
      },
    },
    {
      key: 'channels',
      header: 'Каналы',
      render: (row) =>
        row.channelNames.length > 0 ? (
          <span className="text-ink-soft">{row.channelNames.join(', ')}</span>
        ) : (
          /**
           * Агент, не привязанный ни к одному каналу, ни за что не
           * отвечает. Отдельная колонка нужна именно поэтому: иначе такой
           * агент выглядит рабочим, а бот молчит, и причина не видна.
           */
          <span className="text-copper">ни к одному каналу не привязан</span>
        ),
    },
    {
      key: 'updated',
      header: 'Изменён',
      align: 'right',
      render: (row) => <span className="text-muted">{formatRelative(row.updatedAt)}</span>,
    },
  ]

  const publishedPrompts = (prompts.data ?? []).filter((item) => item.publishedVersion !== null)

  return (
    <>
      <PageHeader
        title="Агенты"
        description="Конфигурация исполнителя диалога: модель, лимиты и закреплённые версии промптов. У неё тоже версии, сравнение и откат."
        actions={
          canEdit && (
            <Button variant="primary" onClick={() => setShowForm((value) => !value)}>
              {showForm ? 'Свернуть' : 'Новый агент'}
            </Button>
          )
        }
      />

      {showForm && canEdit && (
        <Panel title="Новый агент" className="mb-5">
          {/*
            Экран больше не требует завести промпт заранее. Раньше на этом
            месте стояло «Сначала нужен промпт» с кнопкой в другой раздел,
            и первый агент в пустом воркспейсе создавался в два захода —
            ровно то неудобство, что было в старой админке. Требование
            «у агента обязательна системная инструкция» никуда не делось:
            теперь текст пишется здесь же, а промпт с первой версией
            заводит сервер, одной транзакцией с агентом.
          */}
          <AgentForm
            prompts={publishedPrompts.map((item) => ({
              id: item.publishedVersion?.id ?? '',
              label: `${item.name} · версия ${item.publishedVersion?.version ?? ''}`,
            }))}
            loading={create.isPending}
            error={create.error}
            onSubmit={(input) => create.mutate(input)}
          />
        </Panel>
      )}

      {agents.error && <ErrorPanel error={agents.error} onRetry={() => void agents.refetch()} />}

      <DataTable
        rows={agents.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        isLoading={agents.isLoading}
        isRefreshing={agents.isFetching && !agents.isLoading}
        empty={
          <EmptyState
            title="Агентов пока нет"
            description={
              canEdit
                ? 'Агент собирается из промпта и настроек. Привязать его к каналу можно на экране самого агента.'
                : 'Создавать агентов может администратор воркспейса.'
            }
          />
        }
      />
    </>
  )
}

function AgentForm({
  prompts,
  loading,
  error,
  onSubmit,
}: {
  prompts: { id: string; label: string }[]
  loading: boolean
  error: unknown
  onSubmit: (input: CreateFormValue) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState('')
  const [promptVersionId, setPromptVersionId] = useState(prompts[0]?.id ?? '')

  /**
   * Режим по умолчанию — «написать текст». Так выглядит первый агент
   * в пустом воркспейсе, и заставлять человека сперва заводить промпт
   * в другом экране ради этого не нужно. Выбор существующего остаётся
   * рядом: он нужен, когда тот же текст берут для второго агента, —
   * ровно то, ради чего промпты и сделаны отдельной сущностью.
   */
  const [mode, setMode] = useState<'new' | 'existing'>('new')

  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit(
      mode === 'new'
        ? { mode, name: name.trim(), description: description.trim(), body }
        : { mode, name: name.trim(), description: description.trim(), promptVersionId },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Название"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={120}
          placeholder="Продавец LATAM"
        />
        <Field
          label="Пояснение"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={500}
          placeholder="Испанский, оплата при получении"
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="label-xs">Системная инструкция</span>
          <div className="ml-auto inline-flex rounded-lg border border-line p-0.5">
            <button
              type="button"
              onClick={() => setMode('new')}
              aria-pressed={mode === 'new'}
              className={cn(
                'rounded-md px-3 py-1 text-[13px] transition-colors',
                mode === 'new' ? 'bg-accent-soft font-medium text-accent' : 'text-muted',
              )}
            >
              Написать текст
            </button>
            <button
              type="button"
              onClick={() => setMode('existing')}
              aria-pressed={mode === 'existing'}
              disabled={prompts.length === 0}
              className={cn(
                'rounded-md px-3 py-1 text-[13px] transition-colors disabled:opacity-50',
                mode === 'existing' ? 'bg-accent-soft font-medium text-accent' : 'text-muted',
              )}
            >
              Взять готовый
            </button>
          </div>
        </div>

        {mode === 'new' ? (
          <>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              required
              rows={10}
              maxLength={PROMPT_BODY_MAX}
              placeholder="Ты продавец. Отвечай по-испански, коротко и по делу…"
              className="w-full rounded-lg border border-line bg-raised px-3 py-2 font-mono text-[13px] text-ink"
            />
            <p className="text-xs text-muted">
              Промпт и его первая версия создадутся вместе с агентом. Дальше текст правится
              в разделе «Промпты», и каждая правка — новая версия.
            </p>
          </>
        ) : (
          <SelectField
            label="Промпт"
            value={promptVersionId}
            onChange={(event) => setPromptVersionId(event.target.value)}
            hint="Закрепляется конкретной версией: правка текста промпта не изменит поведение этого агента задним числом"
          >
            {prompts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </SelectField>
        )}
      </div>

      {error != null && <ErrorPanel error={error} />}

      <div>
        <Button type="submit" variant="primary" loading={loading}>
          Создать и опубликовать
        </Button>
      </div>
    </form>
  )
}
